import React, { useEffect, useState, useRef } from 'react';
import useContextMenu from '../../../hooks/useContextMenu';
import { useStore } from 'react-redux';

const ContextMenu = ({
    menuItems,
    onMenuItemClick,
    onMenuItemBlur,
    element,
    selectedCompany = null,
    selectedLs = null,
}) => {
    const store = useStore().getState();
    const {
        auth: { user },
    } = store;
    const { xPos, yPos, showMenu } = useContextMenu(element);
    const [options, setOptions] = useState([]);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveSubmenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!showMenu) {
            document.addEventListener('click', () => {
                onMenuItemBlur();
                setActiveSubmenu(null);
            });
        }
    }, [showMenu]);

    useEffect(() => {
        if (selectedLs && (selectedLs?.isPausedSequencing || selectedLs?.isStoppedSequencing)) {
            let tmpMenus = menuItems?.map((menu) => {
                if (menu?.value === 'pause' && selectedLs?.isPausedSequencing) {
                    return { ...menu, value: 'unpause', label: 'Unpause Sequencing' };
                } else if (menu.value === 'pause' && !selectedLs?.isPausedSequencing) {
                    return { ...menu, value: 'pause', label: 'Pause Sequencing' };
                } else if (menu.value === 'stop' && selectedLs?.isStoppedSequencing) {
                    return { ...menu, value: 'resume', label: 'Resume Sequencing' };
                } else {
                    return { ...menu, value: 'stop', label: 'Stop Sequencing' };
                }
            });
            setOptions(tmpMenus);
        } else {
            setOptions(menuItems);
        }
    }, [selectedLs]);

    useEffect(() => {
        if (selectedCompany && (selectedCompany?.isPausedSequencing || selectedCompany?.isStoppedSequencing)) {
            let tmpMenus = menuItems?.map((menu) => {
                if (menu?.value === 'pause' && selectedCompany?.isPausedSequencing) {
                    return { ...menu, value: 'unpause', label: 'Unpause Sequencing' };
                } else if (menu.value === 'pause' && !selectedCompany?.isPausedSequencing) {
                    return { ...menu, value: 'pause', label: 'Pause Sequencing' };
                } else if (menu.value === 'stop' && selectedCompany?.isStoppedSequencing) {
                    return { ...menu, value: 'resume', label: 'Resume Sequencing' };
                } else if (menu.value === 'stop' && !selectedCompany?.isStoppedSequencing) {
                    return { ...menu, value: 'stop', label: 'Stop Sequencing' };
                }
                return menu;
            });
            setOptions(tmpMenus);
        } else {
            setOptions(menuItems);
        }
    }, [selectedCompany]);

    const handleMenuItemClick = (item, parentItem = null) => {
        if (item.children) {
            setActiveSubmenu(item.value);
        } else {
            const itemToSend = parentItem ? { ...item, parent: parentItem } : item;
            onMenuItemClick(itemToSend);
            setActiveSubmenu(null);
        }
    };

    const renderMenuItem = (item, index, isSubmenu = false) => {
        const hasChildren = item.children && item.children.length > 0;
        const isActiveSubmenu = activeSubmenu === item.value;

        return (
            <li
                key={index}
                className={`context-menu-item ${hasChildren ? 'has-children' : ''}`}
                onClick={(e) => {
                    if (!hasChildren) {
                        handleMenuItemClick(item, isSubmenu ? item.parent : null);
                    }
                    e.stopPropagation();
                }}
                onMouseEnter={() => {
                    if (hasChildren) setActiveSubmenu(item.value);
                }}
                onMouseLeave={() => {
                    if (hasChildren) setActiveSubmenu(null);
                }}
            >
                {item.label}
                {hasChildren && <span className="submenu-indicator">›</span>}

                {hasChildren && (
                    <ul
                        className={`context-menu-submenu ${isActiveSubmenu ? 'active' : ''}`}
                    >
                        {item.children.map((child, childIndex) => (
                            <li
                                key={childIndex}
                                className="context-menu-item"
                                onClick={(e) => {
                                    handleMenuItemClick(child, item);
                                    e.stopPropagation();
                                }}
                            >
                                {child.label}
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <>
            {showMenu ? (
                <div
                    ref={menuRef}
                    tabIndex={0}
                    className="context-menu-container"
                    style={{ top: yPos, left: xPos }}
                >
                    <ul className="context-menu-list">
                        {options.map((item, index) => renderMenuItem(item, index))}
                    </ul>
                </div>
            ) : null}

            <style>{`
                .context-menu-container {
                    position: absolute;
                    z-index: 1000;
                    background-color: white;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                    min-width: 200px;
                    font-family: sans-serif;
                }

                .context-menu-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                }

                .context-menu-item {
                    padding: 12px 16px;
                    cursor: pointer;
                    white-space: nowrap;
                    position: relative;
                    transition: background 0.2s;
                    color: #214466;
                    font-size: 14px;
                    list-style-type: none;
                }

                .context-menu-item:hover {
                    background-color: #214466;
                    color: white;
                }

                .submenu-indicator {
                    position: absolute;
                    right: 12px;
                    color: inherit;
                }

                .context-menu-submenu {
                    position: absolute;
                    top: 0;
                    left: 100%;
                    background-color: white;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                    min-width: 200px;
                    padding: 0;
                    margin: 0;
                    display: none;
                    z-index: 1001;
                }

                .context-menu-submenu.active {
                    display: block;
                }

                .has-children {
                    position: relative;
                    padding-right: 32px;
                }
            `}</style>
        </>
    );
};

export default ContextMenu;
